import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftCircle, ArrowRightCircle } from "lucide-react";
import { toast } from 'react-toastify';
import Button from '../../../components/UI/Button';
import ConfigureExperience from '../../../components/Experience/ConfigureExperience';
import Loader from '../../../components/UI/Loader/Loader';
import { getFlattenExperienceJSON, getObjFromHtml, transformExperienceJSON, updateHtmlTemplate } from "../../../utils/helper";
import { editExperience } from '../../../utils/APIs';
import { useUserContext } from '../../../contexts/UserContext';
import { useContentfulContext } from '../../../contexts/ContentfulContext';

const BulkPreview = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { experiences } = location.state || {};
    const { userData } = useUserContext();
    const { enums, content } = useContentfulContext();

    const [selectedExpData, setSelectedExpData] = useState(experiences?.[0]);
    const [selectedExpName, setSelectedExpName] = useState(experiences?.[0]?.name);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditEnable, setIsEditEnable] = useState(false);
    const [fieldValues, setFieldValues] = useState({});
    const [transformedExperienceJSON, setTransformedExperienceJSON] = useState({});
    const [dynamicFields, setDynamicFields] = useState({})
    const [arrayFields, setArrayFields] = useState({})
    const [validationError, setValidationError] = useState("");

    useEffect(() => {
        if (!experiences || !experiences?.length) {
            navigate('/experience/manage-experiences');
        } else {
            setSelectedExpData(experiences[0]);
            setSelectedExpName(experiences[0]?.name);
        }
    }, [experiences]);

    const handleNext = () => {
        setIsLoading(true);
        setIsEditEnable(false);
        setFieldValues({})
        setTransformedExperienceJSON({})
        setDynamicFields({})
        setSelectedExpData((prev) => {
            const currentIndex = experiences.indexOf(prev);
            const newIndex = Math.min(currentIndex + 1, experiences.length - 1);
            const newExpData = experiences[newIndex];
            setSelectedExpName(newExpData.name);
            return newExpData;
        });
        setTimeout(() => setIsLoading(false), 500);
    };

    const handlePrevious = () => {
        setIsLoading(true);
        setIsEditEnable(false);
        setFieldValues({})
        setTransformedExperienceJSON({})
        setDynamicFields({})
        setSelectedExpData((prev) => {
            const currentIndex = experiences.indexOf(prev);
            const newIndex = Math.max(currentIndex - 1, 0);
            const newExpData = experiences[newIndex];
            setSelectedExpName(newExpData.name);
            return newExpData;
        });
        setTimeout(() => setIsLoading(false), 500);
    };

    useEffect(() => {
        if (selectedExpData && selectedExpData.templates?.[0]) {
            let flatExperienceJson;
            if (selectedExpData.templates[0].type === enums?.templateTypes.json_a_to_html.id) {
                const parsedJsonSchema = JSON.parse(selectedExpData?.templates[0]?.json_schema)
                flatExperienceJson = getObjFromHtml(selectedExpData?.experience_json, parsedJsonSchema.config_obj_key)
            } else {
                flatExperienceJson = JSON.parse(selectedExpData?.experience_json)
            }

            const filtered = JSON.parse(selectedExpData.templates[0].config_json).filter((x) => x.input.input_type === "array")
            const arrToObj = filtered.reduce((acc, curr) => {
                acc[curr.id] = curr;
                return acc;
            }, {});
            setArrayFields(arrToObj);

            let temps = {}
            let flattenJSON = getFlattenExperienceJSON(flatExperienceJson, arrayFields);

            for (const key in flattenJSON) {
                if (flattenJSON.hasOwnProperty(key)) {
                    if (key.includes('.0.')) {
                        flattenJSON[key.replace('.0.', '[0].')] = flattenJSON[key]
                        delete flattenJSON[key];
                    } else if (key.includes('.1.')) {
                        flattenJSON[key.replace('.1.', '[1].')] = flattenJSON[key]
                        delete flattenJSON[key];
                    } else if (key.includes('.2.')) {
                        flattenJSON[key.replace('.1.', '[1].')] = flattenJSON[key]
                        delete flattenJSON[key];
                    }
                }

                const element = JSON.parse(selectedExpData?.templates[0].config_json).find((x) => {
                    return x.id === key;
                })

                if (element?.input?.input_type === "dynamic-text") {
                    temps[element.id] = flattenJSON[key]
                    flattenJSON[key] = element.input?.input_default_value;
                }

            }

            setDynamicFields(temps)
            setFieldValues(flattenJSON);
        }
    }, [selectedExpData]);

    const editExperienceHandler = async () => {
        try {
            if (!selectedExpName) {
                toast.error(content.BULK_PREVIEW_CONTENT.errors.exp_name_required);
                return;
            }

            const isValidate = handleValidateJson();
            if (isValidate.length > 0) return;

            setIsLoading(true);
            const updateHTML = updateHtmlTemplate(selectedExpData?.templates?.[0]?.html || "", fieldValues)

            const payload = {
                name: selectedExpName,
                experience_json: selectedExpData?.templates?.[0]?.type === enums?.templateTypes.json_a_to_html.id ? updateHTML : JSON.stringify(transformExperienceJSON(fieldValues, dynamicFields))
            };

            await editExperience(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                selectedExpData._id,
                userData?.defaultAdobeIO._id,
                userData?.defaultContentful._id,
                userData?.defaultTealiumKey?._id,
                payload
            );
            // setExperience(result.data)
            setIsEditEnable(false);
            toast.success(content.BULK_PREVIEW_CONTENT.success.exp_updated);
        } catch (error) {
            toast.error(error?.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleValidateJson = () => {
        setValidationError("");
        const config_elems = [];
        JSON.parse(selectedExpData.templates[0].config_json).forEach((el) => {
            el.error = "";
            config_elems.push({ ...el, id: el.id });
        });
        const newConfigElems = [...config_elems];
        const errs = [];

        for (let item of newConfigElems) {
            let val = "";
            if (dynamicFields[item.id] !== undefined) {
                val = dynamicFields[item.id]
            } else {
                val = fieldValues[item.id];
            }

            const rules = item.rules;
            if (rules.is_mandatory) {
                if (val?.length == 0) {
                    const message = content.BULK_PREVIEW_CONTENT.errors.field_required;
                    item.error = message;
                    errs.push(`${item.id}: ${message}`)
                } else {
                    item.error = "";
                }
            }
            if (rules.min_length && rules.max_length && item.error.length == 0) {
                if (val.length > rules.max_length || val.length < rules.min_length) {
                    const message = `Min length: ${rules.min_length} and Max length: ${rules.max_length}`;
                    item.error = message;
                    errs.push(`${item.id}: ${message}`);
                } else {
                    item.error = "";
                }
            }
        }

        if (errs.length > 0) {
            toast.error(content.BULK_PREVIEW_CONTENT.errors.fix_validation_errs);
            setValidationError(content.BULK_PREVIEW_CONTENT.errors.fix_validation_errs);
        }
        return errs;
    }

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                navigate('/experience/manage-experiences');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);


    return (
        <>
            <div className='w-full mx-auto overflow-auto sticky top-[65px] z-[15]'>
                {isLoading && <Loader />}
                {experiences?.map((exp, index) => (
                    exp === selectedExpData && (
                        <React.Fragment key={exp?._id || index}>
                            <div className='py-3 px-6 md:px-8 w-full bg-white/95 backdrop-blur border-b border-stride-border sticky top-0 mb-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-xs'>
                                <div className='w-full md:w-1/3 lg:w-1/4'>
                                    <input
                                        value={selectedExpName}
                                        type="text"
                                        placeholder="Experience name"
                                        id="experience_name"
                                        name="experience_name"
                                        disabled={!isEditEnable}
                                        onChange={(e) => setSelectedExpName(e.target.value)}
                                        className="block px-3 py-2 w-full text-sm font-medium text-stride-heading bg-stride-bg rounded-lg border border-stride-border focus:bg-white focus:outline-none focus:ring-1 focus:ring-stride-primary focus:border-stride-primary disabled:opacity-75 disabled:bg-transparent disabled:border-transparent transition-all"
                                    />
                                </div>
                                <div className='flex justify-center items-center gap-3 w-full md:w-auto order-2 md:order-none'>
                                    <button
                                        type="button"
                                        disabled={selectedExpData === experiences[0]}
                                        onClick={selectedExpData !== experiences[0] ? handlePrevious : null}
                                        className="p-1 text-stride-muted hover:text-stride-heading disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        aria-label="Previous experience"
                                    >
                                        <ArrowLeftCircle size={28} />
                                    </button>
                                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-stride-bg text-stride-heading border border-stride-border">
                                        {index + 1} / {experiences?.length}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={selectedExpData === experiences[experiences.length - 1]}
                                        onClick={selectedExpData !== experiences[experiences.length - 1] ? handleNext : null}
                                        className="p-1 text-stride-muted hover:text-stride-heading disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        aria-label="Next experience"
                                    >
                                        <ArrowRightCircle size={28} />
                                    </button>
                                </div>
                                <div className='w-full md:w-auto flex justify-end items-center gap-2'>
                                    {exp.templates?.[0] && (
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => setIsEditEnable(!isEditEnable)}
                                        >
                                            {isEditEnable ? content?.BULK_PREVIEW_CONTENT?.cancel_btn || "Cancel" : content?.BULK_PREVIEW_CONTENT?.edit_btn || "Edit"}
                                        </Button>
                                    )}
                                    {isEditEnable && (
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={editExperienceHandler}
                                        >
                                            {content?.BULK_PREVIEW_CONTENT?.save_btn || "Save"}
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {Object.keys(fieldValues).length > 0 && exp.templates?.[0] ? (
                                <div className='h-full max-h-[calc(100vh-80px)] px-4 md:px-8'>
                                    <ConfigureExperience
                                        isEditEnable={isEditEnable}
                                        actionText={content.BULK_PREVIEW_CONTENT.title}
                                        selectedTemplate={exp.templates[0]}
                                        fieldValues={fieldValues}
                                        setFieldValues={setFieldValues}
                                        transformedExperienceJSON={transformedExperienceJSON}
                                        setTransformedExperienceJSON={setTransformedExperienceJSON}
                                        dynamicFields={dynamicFields}
                                        setDynamicFields={setDynamicFields}
                                        validationError={validationError}
                                    />
                                </div>
                            ) : (
                                <p className='text-center text-xl text-stride-muted mt-40 font-medium'>
                                    {content?.BULK_PREVIEW_CONTENT?.template_not_found || "Template not found"}
                                </p>
                            )}
                        </React.Fragment>
                    )
                ))}
            </div>
        </>
    );
}

export default BulkPreview